import styled, { css } from 'styled-components';

import { media } from '../../styles/breakpoints';

export const Container = styled.div`
  width: 100%;
`;

export const PageTitle = styled.h2`
  font-size: 22px;
  font-family: 'Roboto', sans-serif;
  font-weight: bold;
  color: rgba(0, 0, 0, 0.85);
  margin: 0 0 4px 0;
`;

export const PageSubtitle = styled.p`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.45);
  margin: 0 0 24px 0;
`;

export const TabsContainer = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 24px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  ${media.sm} {
    gap: 0;
    margin-bottom: 16px;
  }

  /* Hide scrollbar but keep functionality */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

type TabButtonProps = {
  $isActive: boolean;
};

export const TabButton = styled.button<TabButtonProps>`
  padding: 12px 20px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.55);
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  white-space: nowrap;
  flex-shrink: 0;

  ${media.sm} {
    padding: 10px 14px;
    font-size: 13px;
  }

  &:hover {
    color: rgba(0, 0, 0, 0.85);
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      color: var(--primary);

      &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        height: 2px;
        background: var(--primary);
      }
    `}
`;

export const TabContent = styled.div`
  background: #fff;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  padding: 24px;

  ${media.sm} {
    padding: 16px;
    border-radius: 6px;
  }
`;

export const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  margin: 0 0 16px 0;
`;

export const SectionDescription = styled.p`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
  margin: 0 0 20px 0;
`;

// Team section styles
export const TeamHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  gap: 16px;

  ${media.sm} {
    flex-direction: column;
    gap: 12px;
  }
`;

export const InviteButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  min-height: 44px;
  flex-shrink: 0;

  ${media.sm} {
    width: 100%;
    padding: 12px 16px;
  }

  &:hover {
    background: color-mix(in srgb, var(--primary) 85%, #000);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const UsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const UserCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  gap: 12px;

  ${media.sm} {
    flex-direction: column;
    align-items: stretch;
    padding: 16px;
  }
`;

export const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
`;

export const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--primary);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
`;

export const UserDetails = styled.div`
  min-width: 0;
  flex: 1;
`;

export const UserName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
`;

export const UserEmail = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  word-break: break-all;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const UserStatus = styled.span<{ $isActive: boolean }>`
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ $isActive }) =>
    $isActive ? 'color-mix(in srgb, var(--success) 15%, transparent)' : 'color-mix(in srgb, var(--warning) 15%, transparent)'};
  color: ${({ $isActive }) => ($isActive ? 'var(--success)' : 'var(--warning)')};
`;

export const UserActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  ${media.sm} {
    justify-content: space-between;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
  }
`;

export const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.45);
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
  min-width: 44px;
  min-height: 44px;

  &:hover {
    background: color-mix(in srgb, var(--danger) 10%, transparent);
    color: var(--danger);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const EmptyTeam = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: rgba(0, 0, 0, 0.45);

  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`;

// Form styles
export const FormGroup = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const FormLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 6px;
`;

export const FormInput = styled.input`
  width: 100%;
  max-width: 400px;
  height: 44px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 16px;
  transition: all 0.2s;

  ${media.sm} {
    max-width: 100%;
    font-size: 16px; /* Prevents zoom on iOS */
  }

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--primary) 20%, transparent);
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
`;

export const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 20px;
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  margin-top: 8px;
  min-height: 44px;

  ${media.sm} {
    width: 100%;
    padding: 12px 20px;
  }

  &:hover {
    background: color-mix(in srgb, var(--primary) 85%, #000);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
